"use client";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  
  const [role, setRole] = useState("");
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");

  const [isChangingPw, setIsChangingPw] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [pwStatus, setPwStatus] = useState("");

  // NEW: Mobile Hamburger Menu State
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    setRole(localStorage.getItem("hms_user_role") || "");
    setUserName(localStorage.getItem("hms_user_name") || "Active User");
    setUserId(localStorage.getItem("hms_user_id") || "");
  }, [pathname]);

  // THE KILL SWITCH
  if (pathname === "/") return null;
  if (!role) return null;

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const handlePasswordChange = async (e: any) => {
    e.preventDefault();
    if (newPw.length < 6) {
      setPwStatus("Too short (min 6).");
      return;
    }
    setPwStatus("Saving...");
    try {
      const res = await fetch(`/engine/api/admin/users/${userId}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password: newPw })
      });
      if (res.ok) {
        setPwStatus("Success!");
        setTimeout(() => {
          setIsChangingPw(false);
          setNewPw("");
          setPwStatus("");
        }, 2000);
      } else {
        setPwStatus("Update failed.");
      }
    } catch (err) {
      setPwStatus("Connection error.");
    }
  };

  const navLinks = role === "admin" ? [
    { href: "/admin", label: "Mission Command", icon: "🚀" },
    { href: "/admin/evaluators", label: "Manage Agents", icon: "🕵️" },
    { href: "/admin/clients", label: "Manage Clients", icon: "👔" },
    { href: "/admin/hotels", label: "Hotel Management", icon: "🏨" },
  ] : role === "evaluator" ? [
    { href: "/evaluator", label: "Field Portal", icon: "🎯" },
  ] : [
    { href: "/client", label: "Executive Log", icon: "📊" },
  ];

  return (
    <>
      {/* 1. MOBILE HAMBURGER BUTTON (Hidden on Desktop) */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden fixed top-4 right-4 z-40 p-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white shadow-xl"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
      </button>

      {/* 2. MOBILE BACKDROP OVERLAY */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* 3. THE SIDEBAR (Fixed/Sliding on Mobile -> Rigid/Relative on Desktop) */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-zinc-950 border-r border-zinc-800 h-screen flex flex-col justify-between transform transition-transform duration-300 md:relative md:translate-x-0 ${isMobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}>
        
        <div>
          <div className="p-6 border-b border-zinc-800 mb-4 flex justify-between items-center">
            <div>
              <h1 className="text-xl font-black tracking-widest uppercase text-white">HMS <span className="text-blue-500">Butler</span></h1>
              <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">Intelligence Matrix</p>
            </div>
            {/* Mobile Close 'X' Button */}
            <button onClick={() => setIsMobileOpen(false)} className="md:hidden text-zinc-500 hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <nav className="px-4 space-y-2">
            {navLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Link key={link.href} href={link.href} onClick={() => setIsMobileOpen(false)}>
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold uppercase tracking-widest transition-all ${
                    isActive 
                      ? "bg-blue-600/10 text-blue-500 border border-blue-500/20" 
                      : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300 border border-transparent"
                  }`}>
                    <span>{link.icon}</span>
                    {link.label}
                  </div>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="p-6 border-t border-zinc-800 bg-zinc-950">
          
          <div className="mb-5">
            <div className="text-sm font-bold text-zinc-200 tracking-wide mb-1">
              {userName}
            </div>
            <div className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">
              {role} Account
            </div>
          </div>
          
          {isChangingPw ? (
            <form onSubmit={handlePasswordChange} className="space-y-2">
              <input 
                type="text" 
                autoFocus
                placeholder="New password..." 
                className="w-full bg-zinc-900 border border-zinc-700 p-2 rounded text-xs text-white outline-none focus:border-blue-500"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
              />
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold py-2 rounded uppercase tracking-widest transition-colors">Save</button>
                <button type="button" onClick={() => {setIsChangingPw(false); setPwStatus("");}} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-bold py-2 rounded uppercase tracking-widest transition-colors">Cancel</button>
              </div>
              {pwStatus && <div className={`text-[10px] text-center font-bold ${pwStatus.includes("Success") ? "text-green-500" : "text-yellow-500"}`}>{pwStatus}</div>}
            </form>
          ) : (
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => setIsChangingPw(true)}
                className="text-left text-xs font-bold text-zinc-500 hover:text-blue-400 uppercase tracking-widest transition-colors w-fit flex items-center gap-2"
              >
                <span>🔑</span> Change Password
              </button>
              <button 
                onClick={handleLogout}
                className="text-left text-xs font-bold text-zinc-500 hover:text-red-500 uppercase tracking-widest transition-colors w-fit flex items-center gap-2"
              >
                <span>🚪</span> Disconnect
              </button>
            </div>
          )}

        </div>
      </aside>
    </>
  );
}
